import { DecodeWorkerPool } from "../workers/WorkerPool";
import { createWorker } from "../main/WorkerCreator";
import { HTTP2_PROXY_URL } from "./RcsConstants";

/**
 * Manages the shared RCS fetch worker and its wiring to the DecodeWorkerPool.
 * Uses reference counting so the workers stay alive as long as at least one
 * RcsFileLoader is active, and are torn down when the last one unregisters.
 *
 * Stats counters (`inFlightRequests`, `inFlightRequestsSize`, `totalReceivedBytes`)
 * live here because the pool owns the fetch lifecycle.
 */
class RcsWorkerPool {

  constructor() {
    /** @type {any} */
    this._fetchWorker = null;
    /** @type {boolean} */
    this._initialized = false;
    /** @type {AbortController|null} Removes worker message listeners on teardown. */
    this._listenerAbort = null;
    /** @type {number} */
    this._refCount = 0;
    /** @type {Map<number, {resolve: Function, reject: Function, length: number}>} */
    this._pendingRequests = new Map();
    /** @type {number} */
    this._nextRequestId = 0;
    /** @type {number} */
    this.totalReceivedBytes = 0;
    /** @type {number} */
    this.inFlightRequests = 0;
    /** @type {number} */
    this.inFlightRequestsSize = 0;
  }

  /**
   * Register a consumer (RcsFileLoader). Initializes workers on first call.
   */
  register() {
    this._refCount++;
    if (!this._initialized) this._initWorkers();
  }

  /**
   * Unregister a consumer. Tears down workers when the last consumer leaves.
   */
  unregister() {
    if (--this._refCount <= 0) this._teardown();
  }

  /**
   * Allocate a request ID and return a promise that resolves when the decode
   * worker posts the result.  Sends the fetch-and-decode operation to the
   * shared fetch worker.
   * @param {{length: number, [key: string]: any}} operationData - Payload forwarded to the fetch worker
   * @returns {Promise<any>}
   */
  dispatchFetchAndDecode(operationData) {
    if (!this._initialized) {
      throw new Error('RcsWorkerPool.dispatchFetchAndDecode() called before register()');
    }

    const requestId = this._nextRequestId++;
    this.inFlightRequests++;
    this.inFlightRequestsSize += operationData.length;

    const promise = new Promise((resolve, reject) => {
      this._pendingRequests.set(requestId, { resolve, reject, length: operationData.length });
    });

    this._fetchWorker.doOperation({
      ...operationData,
      operation: 'RCS_FETCH_AND_DECODE',
      requestId
    });

    return promise.finally(() => {
      this.inFlightRequests--;
      this.inFlightRequestsSize -= operationData.length;
    });
  }

  // ---- private ----

  _initWorkers() {
    if (this._initialized) return;
    this._initialized = true;

    // Listeners added below are tied to this signal so _teardown can remove
    // them all at once. This matters for the decode workers: they belong to
    // the shared DecodeWorkerPool and can outlive our teardown (e.g. while OTG
    // still holds a reference), so their listeners would otherwise accumulate
    // one set per register/teardown cycle.
    this._listenerAbort = new AbortController();
    const { signal } = this._listenerAbort;

    DecodeWorkerPool.register();

    const fetchWorker = createWorker('rcs-fetch');
    this._fetchWorker = fetchWorker;

    const decodeWorkers = /** @type {any[]} */DecodeWorkerPool.getWorkers();
    const portsForFetchWorker = [];

    for (const decodeWorker of decodeWorkers) {
      const channel = new MessageChannel();

      decodeWorker.doOperation(
        { operation: 'INSTALL_RCS_DECODE_PORT', port: channel.port1 },
        [channel.port1]
      );

      portsForFetchWorker.push(channel.port2);
    }

    fetchWorker.doOperation(
      { operation: 'INSTALL_DECODE_PORTS', ports: portsForFetchWorker },
      portsForFetchWorker
    );

    // Workers initialise their batcher from the bundled constant. If the
    // main thread has since changed the URL (e.g. tests set it to null),
    // push the current value so the workers stay in sync.
    fetchWorker.doOperation(
      { operation: 'RCS_SET_PROXY', proxyUrl: HTTP2_PROXY_URL }
    );

    fetchWorker.addEventListener('message', (/** @type {MessageEvent} */event) => {
      const data = event.data;
      if (data.bytesReceived !== undefined) {
        this.totalReceivedBytes += data.bytesReceived;
        return;
      }
      if (data.rcsError) {
        const pending = this._pendingRequests.get(data.rcsError.requestId);
        if (pending) {
          this._pendingRequests.delete(data.rcsError.requestId);
          pending.reject(new Error(data.rcsError.message));
        }
      }
    }, { signal });

    for (const decodeWorker of decodeWorkers) {
      decodeWorker.addEventListener('message', (/** @type {MessageEvent} */event) => {
        const data = event.data;
        if (data.rcsDecodeResult) {
          const pending = this._pendingRequests.get(data.requestId);
          if (pending) {
            this._pendingRequests.delete(data.requestId);
            pending.resolve(data.rcsDecodeResult);
          }
        } else if (data.rcsDecodeError) {
          const pending = this._pendingRequests.get(data.requestId);
          if (pending) {
            this._pendingRequests.delete(data.requestId);
            pending.reject(new Error(data.rcsDecodeError.message));
          }
        }
      }, { signal });
    }
  }

  _teardown() {
    // Reset the ref count first so an over-call of unregister() won't result in a negative refCount.
    this._refCount = 0;

    if (!this._initialized) return;

    // Remove all message listeners registered in _initWorkers.
    this._listenerAbort?.abort();
    this._listenerAbort = null;

    if (this._fetchWorker) {
      this._fetchWorker.terminate();
      this._fetchWorker = null;
    }

    DecodeWorkerPool.unregister();

    for (const [, pending] of this._pendingRequests) {
      pending.reject(new Error('RCS workers torn down'));
    }
    this._pendingRequests.clear();
    this._initialized = false;
  }
}

export const rcsWorkerPool = new RcsWorkerPool();